/**
 * Provider-Konfiguration für die BpWeb-App.
 * Nutzt provideLuzulaApp() um alle Library-Tokens in einem Aufruf zu konfigurieren.
 */

import { Provider } from '@angular/core';
import {
	provideLuzulaApp,
	ProjectRolesConfig,
	SyncProgressComponent,
	DownloadProgressComponent,
	OfflineDetailComponent,
	WdFilterComponent,
	ConfirmService
} from '@luzula/luzula-lib';
import { BohrpunktHelpExtensionHandler } from '../bohrpunkte/help/help-extension.handler';
import { AuthService } from '../auth.service';
import { ProjectService } from '../project.service';
import { DataService } from '../data.service';
import { FilterService } from '../filter.service';
import { NavbarService } from '../navbar.service';
import { KeycloakAdminService } from '../keycloak-admin.service';
import { Values } from '../bohrpunkte/filter/application.filter';
import { EvaluationService } from '../bohrpunkte/evaluation.service';
import { ValidationService } from '../bohrpunkte/validation.service';
import { OptionService } from '../bohrpunkte/option.service';
import { HelpService } from '../bohrpunkte/help/help.service';
import { ListDataProvider } from '../bohrpunkte/list-data-provider';
import { BohrpunktDocumentHandler } from '../bohrpunkte/bohrpunkt-document.handler';
import { dbConnectionConfigs } from './db-connection.config';
import { BohrpunktWmsFieldMappingHandler, BohrpunktGpkgAutoStyleHandler, BohrpunktMapMarkerHandler, BohrpunktProjectAssignmentHandler, BohrpunktDataLifecycleHandler } from '../bohrpunkte/handlers';
import { BohrpunktNavigationHandler } from '../map/bohrpunkt-navigation.handler';
import wmsJson from '../../assets/wms/wms.json';
import pkg from 'package.json';
import { ALL_MIGRATIONS } from '../migrations/all-migrations';
import { CURRENT_VERSION } from '../data-model';
import { SEA_DOCUMENT_GROUPS } from '../sea-documents';

/**
 * Alle Library-Provider für BpWeb.
 * Ersetzt die früheren APP_PROVIDERS, CONTROLS_PROVIDERS und die Handler-Provider aus app.module.ts.
 */
export const APP_PROVIDERS: Provider[] = provideLuzulaApp({
	auth: { provider: AuthService, context: AuthService },
	data: {
		provider: DataService,
		documentHandler: BohrpunktDocumentHandler,
		dbConfigs: dbConnectionConfigs,
	},
	project: { provider: ProjectService },
	filter: {
		provider: FilterService,
		component: WdFilterComponent,
		values: Values,
	},
	navbar: NavbarService,
	services: {
		evaluation: EvaluationService,
		validation: ValidationService,
		option: OptionService,
		help: HelpService,
		helpExtension: BohrpunktHelpExtensionHandler,
		confirm: ConfirmService,
		keycloakAdmin: KeycloakAdminService,
		listData: ListDataProvider,
	},
	components: {
		syncProgress: SyncProgressComponent,
		downloadProgress: DownloadProgressComponent,
		offlineDetail: OfflineDetailComponent,
	},
	handlers: {
		mapMarker: BohrpunktMapMarkerHandler,
		mapNavigation: BohrpunktNavigationHandler,
		wmsFieldMapping: BohrpunktWmsFieldMappingHandler,
		gpkgAutoStyle: BohrpunktGpkgAutoStyleHandler,
		projectAssignment: BohrpunktProjectAssignmentHandler,
		dataLifecycle: BohrpunktDataLifecycleHandler,
	},
	config: {
		sensitiveFields: ['Kartierer', 'Aufnahmedatum'],
		projectRoles: {
			keycloakClient: 'bpweb',
			memberRoles: [
				{
					field: 'bearbeiter', label: 'Bearbeiter', labelPlural: 'Bearbeiter',
					emptyText: 'Keine Bearbeiter verfügbar.', authRole: 'role:kartierer', icon: 'bi-pencil'
				},
				{
					field: 'kontrolleure', label: 'Kontrolleur', labelPlural: 'Kontrolleure',
					emptyText: 'Keine Kontrolleure verfügbar.', authRole: 'role:kontrolleur', icon: 'bi-check-circle',
					dependsOnPermission: 'aendern'
				}
			],
			commentReviewerRoles: ['kontrolleur', 'entwickler'],
			commentNoSeverityRoles: ['kartierer']
		} as ProjectRolesConfig,
		wms: wmsJson.qgsWMSConnections.wms,
		appVersion: pkg.version,
		cleanupVersions: ['1.2.0', '1.5.7'],
		versionStorageKey: 'bpweb_last_version',
		currentDataVersion: CURRENT_VERSION,
		builtInMigrations: ALL_MIGRATIONS,
		routeCacheTargets: ['handbuch', 'listenverwaltung'],
		offlineDocumentGroups: SEA_DOCUMENT_GROUPS,
		dgmApiUrl: 'https://data.waldkonzepte.de',
	},
});
